import { Caution, HelpLayout, HelpSectionBlock } from "@/components/help-layout";
import { subText, table, td, th } from "@/components/table";
import { useMe } from "@/lib/api";

/**
 * Who can do what, and why it is shaped that way.
 *
 * Access is an app-role assignment in Entra, never a setting inside HROps, so
 * the one answer this page has to give well is "which one am I, and who do I
 * ask". The row for the signed-in person is marked.
 */
const ROLES: Array<{ role: string; label: string; opens: string; can: string; not: string }> = [
  {
    role: "HROps.User",
    label: "User",
    opens: "Leavers, Risk flags, Access lists, Organisation",
    can: "Everything HROps does for a leaver, on any record: raise, revert, cancel, re-enable, flag at any severity, retire a flag, keep the access lists, prepare and approve org scenarios.",
    not: "The Operations view, and the Workforce page without the Workforce role.",
  },
  {
    role: "HROps.Workforce",
    label: "Workforce",
    opens: "Workforce",
    can: "Report hires, exits, planned exits, future positions and org-data changes; see every request and the log; edit or withdraw own requests while open; export to Excel.",
    not: "Approve or send back, and see compensation on other people's records, unless also an approver. Nothing outside the Workforce page.",
  },
  {
    role: "HROps.Admin",
    label: "Admin",
    opens: "Everything a User opens, plus Operations",
    can: "The same as User, plus the Operations view: what is half-applied, what last failed, what is not configured.",
    not: "No extra functional rights. Admin is the technical view, not a bigger version of the product.",
  },
];

const SECTIONS = [
  { id: "roles", title: "The three roles" },
  { id: "approvers", title: "Workforce approvers" },
  { id: "me", title: "Which one am I?" },
  { id: "granting", title: "How access is granted" },
  { id: "why", title: "Why there is no read-only role" },
];

export function RolesHelpPage() {
  const me = useMe().data;
  const mine = new Set(me?.app_roles ?? []);
  return (
    <HelpLayout
      title="Roles and access"
      intro="Three roles on the HROps application in Entra, one list of approvers inside the Workforce page. A role opens pages; it does not change what a page does."
      sections={SECTIONS}
    >
      <HelpSectionBlock id="roles" title="The three roles">
        <div className="overflow-x-auto">
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Role</th>
                <th className={th}>Opens</th>
                <th className={th}>Can</th>
                <th className={th}>Cannot</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((r) => (
                <tr key={r.role}>
                  <td className={td}>
                    <span className="text-fg font-medium">{r.label}</span>
                    <div className={subText}>{r.role}</div>
                    {mine.has(r.role) ? (
                      <div className="text-success-fg text-xs">this is you</div>
                    ) : null}
                  </td>
                  <td className={td}>{r.opens}</td>
                  <td className={`${td} text-fg-muted`}>{r.can}</td>
                  <td className={`${td} text-fg-muted`}>{r.not}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={`${subText} mt-2`}>
          Roles add up. Most HR colleagues hold User; the Workforce role is held in addition by the
          people who run the workforce form; Admin is held by IT.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="approvers" title="Workforce approvers">
        <p>
          Within the Workforce page, approving a request and sending it back are limited to a short
          list of named people, the approvers. The list is part of the deployment, not a role in
          Entra, and today it is the HR colleague who owns the workforce process plus IT. Approvers
          also see the compensation on every record; everybody else sees it only on the records they
          submitted or are the responsible HRBP for.
        </p>
        <p className={subText}>
          {me?.workforce_approver
            ? "You are an approver."
            : me?.workforce_enabled
              ? "You are not an approver: you can report and follow requests, not decide them."
              : "You do not hold the Workforce role."}
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="me" title="Which one am I?">
        {me ? (
          <p>
            Signed in as <strong>{me.email}</strong> with{" "}
            {me.app_roles?.length ? (
              <strong>{me.app_roles.join(", ")}</strong>
            ) : (
              <strong>no HROps role</strong>
            )}
            . The menu shows exactly the pages those roles open.
          </p>
        ) : null}
        <Caution>
          A role granted while you are signed in is not picked up until you sign out and back in.
          That is the first thing to try when a menu is missing.
        </Caution>
      </HelpSectionBlock>

      <HelpSectionBlock id="granting" title="How access is granted">
        <p>
          IT assigns the role on the HROps application in Entra. There is no user list inside HROps
          and nobody in HR can grant access to a colleague from the tool; a request for access goes
          to IT, who assigns the role and confirms. The app refuses a sign-in with no role at all.
        </p>
        <p>
          Removing access is the same step in reverse. A person whose role was removed while signed
          in keeps a read-only session until it expires, and cannot change anything.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="why" title="Why there is no read-only role">
        <p>
          The first version had four roles. For a tool used by about fifteen people that was three
          distinctions nobody was going to maintain, and a read-only role cost a branch in every
          screen. HR does the whole job, so every functional capability belongs to HR; the one line
          that matters, acting on somebody else&apos;s record, is covered by the log naming who did
          what. The Workforce role exists because its page holds pay data and an approval step that
          most HR users should not see yet, not because it does something the others cannot.
        </p>
      </HelpSectionBlock>
    </HelpLayout>
  );
}
